import Button from '@mui/material/Button';
import ChartTemplateDetailsStep from './ChartTemplateDetailsStep/ChartTemplateDetailsStep';
import ChartURLInputStep from './ChartURLInputStep';
import { ManageChartTemplateActionType } from '../ManageChartTemplateContext/ManageChartTemplateReducer';
import React, { useEffect, useState } from 'react';
import SimpleModal from 'ui/Modals/SimpleModal';
import Typography from '@mui/material/Typography';
import { useCreateChartTemplate } from './hooks/createChartTemplate';
import { useGetRootChart } from './hooks/getRootChart';
import { useManageChartTemplateContext } from '../ManageChartTemplateContext/ManageChartTemplateContext';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { validateRootChartUrl } from './helpers';
import ConfirmationModal from 'components/SharedComponents/Modals/ConfirmationModal';

const CreateChartTemplateModalStepper = ({
    open,
    setOpen,
}: {
    open: boolean;
    setOpen: (arg: boolean) => void;
}): JSX.Element => {
    const { t } = useTranslation();
    const [step, setStep] = React.useState<1 | 2>(1);
    const theme = useTheme();
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

    const {
        state: {
            authorizedFunctions,
            authorizedSoftwares,
            description,
            preview,
            rootUrl,
            status,
            title,
        },
        dispatch,
    } = useManageChartTemplateContext();
    const [rootUrlInputValue, setRootUrlInputValue] = React.useState(rootUrl);

    const { refetch, data: rootChartData } = useGetRootChart(rootUrlInputValue);
    // TODO [React Query v5, onSuccess deletion] Relying on useEffect for state syncing is deprecated.
    // With no better immediate solution, this is still the recommanded solution. It is still marginaly better
    // than onSuccess. See: https://github.com/TanStack/query/discussions/5279#discussioncomment-5641482
    useEffect(() => {
        if (rootChartData) {
            dispatch({
                type: ManageChartTemplateActionType.ApplyRootChartValues,
                rootChart: rootChartData,
                rootUrl: rootUrlInputValue,
                shouldUpdateTitle: true,
            });
            setStep(2);
        }
    }, [dispatch, rootChartData, setStep, rootUrlInputValue]);

    const onClose = () => {
        setConfirmationModalOpen(true);
    };

    const createChartTemplateMutation = useCreateChartTemplate({
        title,
        description,
        authorizedSoftwares,
        authorizedFunctions,
        preview,
        rootUrl,
        status,
    });

    const onCreateTemplateAction = (): void => {
        createChartTemplateMutation.mutate();
        setOpen(false);
    };

    return (
        <>
            {step === 1 && (
                <SimpleModal
                    additionalButtons={[
                        <Button
                            key="click-fetch-root-chart"
                            onClick={() => refetch()}
                            variant="contained"
                            disabled={!validateRootChartUrl(rootUrlInputValue)}
                        >
                            {t('common.next')}
                        </Button>,
                    ]}
                    open={open && step === 1}
                    maxWidth="md"
                    showCloseButton={false}
                    onClose={onClose}
                    PaperProps={{
                        sx: { backgroundColor: theme.palette.lightGrey },
                    }}
                    dialogTitle={
                        <>
                            <Typography variant="h4">
                                {t('support.chartsTemplates.create')}
                            </Typography>
                            <Typography sx={{ mt: 2 }}>
                                {t(
                                    'support.chartsTemplates.createOrEditModal.chartUrlInput.subtitle',
                                )}
                            </Typography>
                        </>
                    }
                >
                    <ChartURLInputStep
                        rootUrlInputValue={rootUrlInputValue}
                        setRootUrlInputValue={setRootUrlInputValue}
                    />
                </SimpleModal>
            )}
            {step === 2 && (
                <SimpleModal
                    additionalButtons={[
                        <Button key="previous" onClick={() => setStep(1)}>
                            {t('common.previous')}
                        </Button>,
                        <Button
                            key="click-create-template"
                            onClick={onCreateTemplateAction}
                            variant="contained"
                        >
                            {t(
                                'support.chartsTemplates.createOrEditModal.create',
                            )}
                        </Button>,
                    ]}
                    open={open && step === 2}
                    maxWidth="md"
                    onClose={onClose}
                    showCloseButton={false}
                    PaperProps={{
                        sx: { backgroundColor: theme.palette.lightGrey },
                    }}
                    dialogTitle={
                        <>
                            <Typography variant="h4">
                                {t('support.chartsTemplates.create')}
                            </Typography>
                            <Typography sx={{ mt: 2 }}>
                                {t(
                                    'support.chartsTemplates.createOrEditModal.detailsForm.createSubtitle',
                                )}
                            </Typography>
                        </>
                    }
                >
                    <ChartTemplateDetailsStep />
                </SimpleModal>
            )}
            <ConfirmationModal
                isOpen={confirmationModalOpen}
                title={t(
                    'support.chartsTemplates.createOrEditModal.leavingConfirmation',
                )}
                onConfirm={() => {
                    setOpen(false);
                    setConfirmationModalOpen(false);
                }}
                onCancel={() => setConfirmationModalOpen(false)}
            />
        </>
    );
};

export default CreateChartTemplateModalStepper;
